import { useState, type ReactNode } from "react";
import classnames from "classnames";

import Icon from "common/core/icon";
import { Substyle } from "common/core/typography";
import Button from "common/core/button";
import { useId } from "util/html";

import Styles from "./item.module.scss";

type Props = {
  header: ReactNode;
  subheader?: ReactNode;
  centerHeader?: ReactNode;
  initialDisplay?: boolean;
  hideToggle?: boolean;
  children?: ReactNode;
  isResponsive?: boolean;
  showPointerCursor?: boolean;
};

function CollapsibleListItem({
  header,
  subheader,
  centerHeader,
  initialDisplay,
  hideToggle,
  isResponsive,
  children,
  showPointerCursor,
}: Props) {
  const headerId = useId();
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(Boolean(initialDisplay));
  const forceHideToggle = !children || hideToggle;

  const iconCx = classnames(Styles.icon, forceHideToggle && Styles.hiddenIcon);
  const iconName = `caret-${isOpen ? "up" : "down"}`;
  const headerCx = classnames(Styles.header, {
    [Styles.emptyHeader]: forceHideToggle && !showPointerCursor,
    [Styles.headerExpanded]: isOpen,
  });
  const contentCx = classnames(Styles.content, isOpen && Styles.contentExpanded);

  const headerContent = (
    <div id={headerId} className={Styles.headerWrapper}>
      <span className={Styles.headerInner}>
        <Icon className={iconCx} name={iconName} />
        <Substyle textStyle="subtitle">{header}</Substyle>
      </span>
      {centerHeader}
      <span className={Styles.subHeaderInner}>{subheader}</span>
    </div>
  );
  return (
    <li className={classnames(Styles.item, isResponsive && Styles.responsive)}>
      {!forceHideToggle ? (
        <Button
          className={headerCx}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setIsOpen((o) => !o)}
          fullwidth
        >
          {headerContent}
        </Button>
      ) : (
        <div className={headerCx}>{headerContent}</div>
      )}
      {children && (
        <div
          aria-labelledby={headerId}
          role="region"
          className={classnames(contentCx, !isOpen && Styles.hidden)}
          {...(!forceHideToggle && { id: panelId })}
        >
          {isOpen && children}
        </div>
      )}
    </li>
  );
}

export default CollapsibleListItem;
